import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MarketplaceCardResponseDto, ViewModalProps } from "../../../types/types";
import { formatPurseText, getCurrencySymbol } from "../../../utils/opportunities/opportunityAndReadyRoster";
import { formatEnumString, formatSportName, formatWeightClassName } from "../../../utils/utilMethods";

import { ReportAbusePopup } from "../../../components/abuseReport/reportAbusePopup";
import ConfirmModal from "../../../components/common/confirmModal";
import CalendarView from "./calendarView";

const BUTTON_CLASS = "h-9 min-w-9 rounded-3xl px-4 text-[14px] font-medium leading-[20px]";
const DISABLED_BUTTON_CLASS = `${BUTTON_CLASS} cursor-not-allowed bg-gray-300 text-gray-700`;
const SECTION_TITLE_CLASS =
    "border-b border-gray-200 text-[16px] font-semibold leading-[24px] text-gray-500";
const VALUE_CLASS = "break-words text-[16px] font-medium leading-[24px] text-gray-900";

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[14px] font-medium leading-[20px] text-gray-500">{label}</span>
        <span className={VALUE_CLASS}>{value}</span>
    </div>
);

const InfoSection: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <div className="flex flex-col gap-2">
        <h3 className={SECTION_TITLE_CLASS}>{title}</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</div>
    </div>
);

const ViewModal: React.FC<ViewModalProps> = ({
    isOpen,
    onClose,
    viewCard,
    marketplaceId,
    role,
    activeTab,
    loggedInUserName,
    currentMonth,
    setCurrentMonth,
    selectedDates,
    isPublishing,
    confirmPublishPopup,
    setConfirmPublishPopup,
    confirmContactPopup,
    setConfirmContactPopup,
    showReportPopup,
    setShowReportPopup,
    handlePublish,
    handleCreateContact,
    fetchMarketplaceCards,
}) => {
    const { t } = useTranslation('readyRoster');

    const formatDate = (dateString: any) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const isCardPublished = (card: MarketplaceCardResponseDto) => {
        return card?.readyRosterCard?.status === "PUBLISH" || card?.readyRosterCard?.status === "publish";
    };

    const card = viewCard?.readyRosterCard;
    const fighter = card?.fighter;
    const fighterInitials = `${fighter?.firstName?.charAt(0) ?? ""}${fighter?.lastName?.charAt(0) ?? ""}`;
    const fullName = `${fighter?.firstName ?? ""} ${fighter?.lastName ?? ""}`.trim();
    const displayTitle = fighter?.nickname || fullName;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="5xl"
            scrollBehavior="inside"
            classNames={{
                base: "max-w-[1133px] rounded-3xl bg-white",
                closeButton: "top-4 end-4 h-6 w-6 min-w-6 rounded-xl p-1 text-gray-500 hover:bg-gray-100",
            }}
        >
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader className="px-6 pb-0 pt-6 text-[16px] font-medium leading-[24px] text-gray-900">
                    {t("ready_roster_detail")}
                </ModalHeader>

                <ModalBody className="px-6 pb-6 pt-4 font-['Plus_Jakarta_Sans',sans-serif]">
                    <div className="flex flex-col gap-6 xl:flex-row">
                        {/* Left card */}
                        <div className="w-full shrink-0 overflow-hidden rounded-3xl bg-gray-50 xl:min-h-[600px] xl:w-96">
                            <div className="h-72 p-1">
                                {fighter?.profileImage ? (
                                    <img
                                        src={fighter.profileImage}
                                        alt={displayTitle}
                                        className="h-full w-full rounded-[20px] object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-[20px] bg-gray-200 text-[32px] font-bold text-gray-500">
                                        {fighterInitials}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-4 px-4 pb-4 pt-1">
                                <div className="flex flex-col justify-center">
                                    <h4 className="text-[24px] font-bold leading-[32px] text-gray-900">
                                        {displayTitle}
                                    </h4>
                                    {fighter?.nickname && fullName && (
                                        <p className="text-[14px] leading-[20px] text-gray-500">{fullName}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <InfoItem
                                        label={t("date_of_birth")}
                                        value={formatDate(fighter?.dateOfBirth)}
                                    />
                                    <InfoItem
                                        label={t("weight")}
                                        value={`${fighter?.weight ?? ""} ${fighter?.weightUnit ?? ""}`}
                                    />
                                    <InfoItem
                                        label={t("height")}
                                        value={`${fighter?.height ?? ""} ${fighter?.heightUnit ?? ""}`}
                                    />
                                    <InfoItem
                                        label={t("title_holder")}
                                        value={fighter?.titleHolder ? t("yes") : t("no")}
                                    />
                                    <InfoItem label={t("stance")} value={fighter?.stance || "-"} />
                                    <InfoItem
                                        label={t("reach")}
                                        value={`${fighter?.reach ?? ""} ${fighter?.reachUnit ?? ""}`}
                                    />
                                    <InfoItem label={t("team")} value={fighter?.team || "-"} />
                                    <InfoItem label={t("gym")} value={fighter?.gym || "-"} />
                                </div>
                            </div>
                        </div>

                        {/* Middle sections */}
                        <div className="flex min-w-0 flex-1 flex-col gap-6">
                            <InfoSection title={t("financial_information")}>
                                <InfoItem
                                    label={t("purse")}
                                    value={`${formatPurseText(card?.purse ?? "")}${getCurrencySymbol(card?.currency ?? "")}`}
                                />
                            </InfoSection>

                            <InfoSection title={t("match_information")}>
                                <InfoItem
                                    label={t("sport")}
                                    value={formatSportName(card?.sport ?? "") || "-"}
                                />
                                <InfoItem
                                    label={t("level")}
                                    value={formatEnumString(card?.rosterStatus ?? "-")}
                                />
                                <InfoItem
                                    label={t("weight_class")}
                                    value={formatWeightClassName(card?.weightClass ?? "-")}
                                />
                            </InfoSection>

                            <InfoSection title={t("location_travel")}>
                                <InfoItem label={t("location")} value={card?.location || "-"} />
                                <InfoItem
                                    label={t("travel_ready")}
                                    value={card?.travelReady ? t("yes") : t("no")}
                                />
                            </InfoSection>

                            <InfoSection title={t("validity")}>
                                <InfoItem
                                    label={t("created_at")}
                                    value={formatDate(card?.createdAt)}
                                />
                                <InfoItem
                                    label={t("valid_upto")}
                                    value={formatDate(card?.validUpto)}
                                />
                            </InfoSection>
                        </div>

                        {/* Availability */}
                        <div className="flex w-full shrink-0 flex-col gap-2 xl:w-72">
                            <h3 className={SECTION_TITLE_CLASS}>{t("availability")}</h3>
                            <CalendarView
                                currentMonth={currentMonth}
                                selectedDates={selectedDates}
                                setCurrentMonth={setCurrentMonth}
                            />
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter className="px-6 pb-6 pt-0">
                    <div className="flex w-full flex-wrap items-center justify-end gap-2">
                        {activeTab !== "myCards" && loggedInUserName !== fighter?.email && (
                            <>
                                {card?.reported === true ? (
                                    <Button
                                        color="default"
                                        disabled
                                        className={DISABLED_BUTTON_CLASS}
                                    >
                                        {t("reported")}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="light"
                                        color="danger"
                                        onPress={() => setShowReportPopup(true)}
                                        className={BUTTON_CLASS}
                                    >
                                        {t("report_abuse")}
                                    </Button>
                                )}
                            </>
                        )}

                        <Button variant="flat" onClick={onClose} className={BUTTON_CLASS}>
                            {t("close")}
                        </Button>

                        {role === "MANAGER" && activeTab === "all" && (
                            <>
                                {card?.contacted === false && new Date(card.validUpto) >= new Date() && (
                                    <Button
                                        color="primary"
                                        className={BUTTON_CLASS}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmContactPopup(true);
                                        }}
                                    >
                                        {t("contact")}
                                    </Button>
                                )}
                                {card?.contacted === true && (
                                    <Button
                                        color="default"
                                        disabled
                                        className={DISABLED_BUTTON_CLASS}
                                    >
                                        {t("contacted")}
                                    </Button>
                                )}
                            </>
                        )}

                        {role === "FIGHTER" && activeTab === "myCards" && (
                            <Button
                                color={
                                    new Date(card?.validUpto ?? "") < new Date()
                                        ? "danger"
                                        : isCardPublished(viewCard as MarketplaceCardResponseDto)
                                            ? "default"
                                            : "success"
                                }
                                className={BUTTON_CLASS}
                                onClick={() => {
                                    if (
                                        new Date(card?.validUpto ?? "") >= new Date() &&
                                        !isCardPublished(viewCard as MarketplaceCardResponseDto) &&
                                        marketplaceId
                                    ) {
                                        setConfirmPublishPopup(true);
                                    }
                                }}
                                disabled={
                                    new Date(card?.validUpto ?? "") < new Date() ||
                                    isCardPublished(viewCard as MarketplaceCardResponseDto) ||
                                    isPublishing
                                }
                                isLoading={isPublishing}
                            >
                                {new Date(card?.validUpto ?? "") < new Date()
                                    ? t("expired")
                                    : isCardPublished(viewCard as MarketplaceCardResponseDto)
                                        ? t("published")
                                        : isPublishing
                                            ? t("publishing")
                                            : t("publish")}
                            </Button>
                        )}
                    </div>

                    <ConfirmModal
                        isOpen={confirmContactPopup}
                        onClose={() => setConfirmContactPopup(false)}
                        cardTitle={t("confirm_contact")}
                        cardDescription={t("confirm_contact_description")}
                        buttonText={t("confirm")}
                        onConfirm={() => handleCreateContact(viewCard, { stopPropagation: () => { } })}
                    />

                    <ConfirmModal
                        isOpen={confirmPublishPopup}
                        onClose={() => setConfirmPublishPopup(false)}
                        cardTitle={t("confirm_publish")}
                        cardDescription={t("confirm_publish_description")}
                        buttonText={t("confirm")}
                        onConfirm={() => {
                            if (marketplaceId) handlePublish(marketplaceId.toString());
                        }}
                    />

                    {fighter?.id && marketplaceId && (
                        <ReportAbusePopup
                            isOpen={showReportPopup}
                            onClose={() => setShowReportPopup(false)}
                            onSubmit={(reportType, reportDescription) => {
                                setShowReportPopup(false);
                                onClose();
                                if (fetchMarketplaceCards) fetchMarketplaceCards();
                            }}
                            cardId={marketplaceId.toString()}
                            type="MARKETPLACECARDS"
                            reportedTo={fighter.id ?? ""}
                        />
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ViewModal;
